/**
 * 公告：GET/POST /v1/announcements*
 * 响应带 Cache-Control: no-store，前端不长期缓存。
 * 列表/active 支持 ?lang=zh|en；切语言优先用本地 translations，不必重请求。
 */
import { request } from '@/common/services/request';

export type AnnouncementStatus = 'PUBLISHED' | 'ARCHIVED' | 'UNPUBLISHED' | string;

export type AnnouncementReadSource =
  | 'BANNER_DISMISS'
  | 'LIST_OPEN'
  | 'MARK_READ';

export type AnnouncementLang = 'zh' | 'en';

export interface AnnouncementTranslation {
  title?: string;
  body?: string;
  author?: string;
}

export interface AnnouncementItem {
  id: string;
  title: string;
  body: string;
  /** 英文扁平字段（部分接口） */
  titleEn?: string;
  bodyEn?: string;
  /** 中文发稿人 */
  author?: string;
  /** 英文发稿人 */
  authorEn?: string;
  /** 发稿日期 YYYY-MM-DD（非 publishedAt） */
  signedOn?: string;
  /** 后端按请求 lang 预选的展示文案（未必等于当前 UI 语言） */
  displayTitle?: string;
  displayBody?: string;
  displayAuthor?: string;
  displaySignedOn?: string;
  /** 后端认定的 locale */
  locale?: string;
  /** 多语言：{ en, zh, ... }，含 title/body/author */
  translations?: Record<string, AnnouncementTranslation | null | undefined>;
  status: AnnouncementStatus;
  priority?: number;
  publishedAt?: string | null;
  unpublishedAt?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  version?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  /** 仅 JWT 时返回；缺省按未读 */
  read?: boolean;
}

export interface ActiveAnnouncementResponse {
  item: AnnouncementItem | null;
}

export interface UnreadCountResponse {
  count: number;
}

export interface AnnouncementListResponse {
  items: AnnouncementItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface MarkReadResponse {
  ok: boolean;
  id: string;
  read: boolean;
}

/** GET /v1/announcements/active?lang=zh|en — 鉴权可选；登录建议带 JWT */
export const getActiveAnnouncement = (
  lang: AnnouncementLang = 'en'
): Promise<ActiveAnnouncementResponse> => {
  return request({
    url: '/v1/announcements/active',
    method: 'GET',
    data: { lang },
    showError: false,
  });
};

/** GET /v1/announcements/unread-count — 需 JWT */
export const getAnnouncementUnreadCount = (): Promise<UnreadCountResponse> => {
  return request({
    url: '/v1/announcements/unread-count',
    method: 'GET',
    showError: false,
  });
};

/** GET /v1/announcements?page&pageSize&lang=zh|en — 需 JWT，分页历史 */
export const getAnnouncements = (params?: {
  page?: number;
  pageSize?: number;
  lang?: AnnouncementLang;
}): Promise<AnnouncementListResponse> => {
  return request({
    url: '/v1/announcements',
    method: 'GET',
    data: {
      page: params?.page ?? 1,
      pageSize: Math.min(50, Math.max(1, params?.pageSize ?? 10)),
      lang: params?.lang === 'zh' ? 'zh' : 'en',
    },
    showError: false,
  });
};

/** POST /v1/announcements/:id/read — 需 JWT，幂等 */
export const markAnnouncementRead = (
  id: string,
  source: AnnouncementReadSource = 'MARK_READ'
): Promise<MarkReadResponse> => {
  return request({
    url: `/v1/announcements/${encodeURIComponent(id)}/read`,
    method: 'POST',
    data: { source },
    showError: false,
  });
};

/** 未读才展示横幅：无 read 字段或 read === false */
export const isAnnouncementUnread = (item: AnnouncementItem | null | undefined): boolean => {
  if (!item?.id) return false;
  return item.read !== true;
};
